import React, {
  FC,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AlertInformationType } from 'moduleBase/components/alert-information/types/AlertInformationType';
import { Alert } from 'ui/alert/Alert';
import HTMLReactParser from 'html-react-parser';
import { useNetworkService } from 'hooks/use-network-service';
import { Modal } from '@collaborator/npm_modules/dist/components/modal';
import { Button } from 'components/button/Button';
import { translate } from '@collaborator/npm_modules/dist/components/translate';
import { useDispatch } from 'react-redux';
import { appRemoveAlert } from 'moduleBase/actions/remove-alert';

type PropTypes = Omit<AlertInformationType, 'key'> & {
  keyProp: AlertInformationType['key'];
};

const AlertInformation: FC<PropTypes> = ({
  content,
  keyProp,
  variant,
  closeUrl,
  modalHeader,
  modalContent,
  leftButtonText,
  leftButtonUrl,
  rightButtonText,
  rightButtonUrl,
}) => {
  const networkService = useNetworkService();
  const dispatch = useDispatch();

  const [modalVisible, setModalVisible] = useState<boolean>(false);

  const ref = useRef<HTMLDivElement>();

  const isHaveModal = modalHeader && modalContent && content.includes('data-toggle');

  useEffect(() => {
    if (!isHaveModal) {
      return;
    }

    const listener = () => setModalVisible(true);

    const linkEl = ref?.current?.querySelector('[data-toggle="modal"]');
    linkEl?.addEventListener('click', listener);

    // eslint-disable-next-line consistent-return
    return () => {
      linkEl?.removeEventListener('click', listener);
    };
  }, []);

  const handlerClose = (): Promise<void> => new Promise<void>((resolve) => {
    networkService.postSimple<void>(closeUrl, {
      key: keyProp,
    }).then(() => {
      resolve();
      dispatch(appRemoveAlert(keyProp));
    });
  });

  const hideModalHandler = () => {
    setModalVisible(false);
  };

  const renderModal = () => {
    const renderLeftButton = () => {
      if (!leftButtonText) {
        return null;
      }

      const btnText = leftButtonText ?? translate.translate('CD-7840', 'Dismiss');

      if (!leftButtonUrl) {
        return (<Button primary additionalClassName="fs-16" onClick={hideModalHandler}>{btnText}</Button>);
      }

      return (
        <a href={leftButtonUrl} target="_blank" rel="noreferrer">
          <Button primary>{btnText}</Button>
        </a>
      );
    };

    const renderRightButton = () => {
      if (!rightButtonText) {
        return null;
      }

      const btnText = rightButtonText ?? translate.translate('CD-7840', 'Dismiss');

      if (!rightButtonUrl) {
        return (
          <Button primary additionalClassName="fs-16" onClick={hideModalHandler}>
            {btnText}
          </Button>
        );
      }

      return (
        <a href={rightButtonUrl} target="_blank" rel="noreferrer">
          <Button primary>{btnText}</Button>
        </a>
      );
    };

    return (
      <Modal show={modalVisible} onHide={hideModalHandler}>
        <Modal.Title onHide={hideModalHandler}>
          {modalHeader}
        </Modal.Title>
        <Modal.Body>
          <div className="fs-16">
            {/* @ts-ignore */}
            {HTMLReactParser(modalContent)}
          </div>
        </Modal.Body>
        <Modal.Footer
          className="d-flex justify-content-sm-center flex-column flex-sm-row flex-wrap m-b-30 m-l-15 m-r-15 gap-10px"
        >
          {renderLeftButton()}
          {renderRightButton()}
        </Modal.Footer>
      </Modal>
    );
  };

  return (
    <Alert variant={variant} onClose={handlerClose} dismissible ref={ref}>
      {HTMLReactParser(content)}
      {isHaveModal && renderModal()}
    </Alert>
  );
};

export { AlertInformation };
